<template>
  <main class="homepage">
    <Promotions
      class="homepage__promotions homepage__section homepage__section--md"
      :promotions="promotions || undefined"
      is-fluid
    />
    <GridContainer>
      <HomepageCategories class="homepage__section homepage__section--md" />
      <ThemeTeasers class="homepage__section homepage__section--md" />

      <BannerTeaser
        class="homepage__section homepage__section--md coupon-banner"
        image-xs="/content/content-images/homepage/coupon/5181dc35597482bd536410fa453b0ea4/aktionsteaser-s@2x.png"
        image-sm="/content/content-images/homepage/coupon/72a42e08aead662bf703d80f79348685/aktionsteaser-m@2x.png"
        image-md="/content/content-images/homepage/coupon/f1d509e1477d38b02c74ade25985bba7/aktionsteaser-l@2x.png"
        alt="Rabatt-Aktionen - Jetzt Vorteile sichern!"
        sizes="xs:100vw sm:120vw md:100vw lg:100vw xl:1440px"
        lazy
      >
        <template #default>
          <Heading
            class="coupon-banner__heading"
            tag="h2"
            size="md"
            font-weight="ultra"
            spaces-bottom
          >
            Aktionen nutzen und Vorteile sichern!
          </Heading>
          <Heading
            font-weight="regular"
            spaces-bottom
            tag="h3"
            class="coupon-banner__subheading"
          >
            Nutze aktuelle Aktionsangebote und löse Gutscheine hier direkt ein.
          </Heading>
          <link-button
            to="/preisvorteile-sichern"
            primary
            tag="nuxt-link"
            class="btn--outline"
          >
            Jetzt Vorteile sichern!
          </link-button>
        </template>
      </BannerTeaser>

      <HomepageHighlights class="homepage__section" />
      <BrandWallpaper class="homepage__section homepage__section--md" />
      <SustainableProducts class="homepage__section" />
      <RecommendedProducts class="homepage__section" />
      <Services class="homepage__section--md" />
      <WarrantyLabel />
    </GridContainer>
  </main>
</template>

<script setup lang="ts">
import { useLazyAsyncData } from '#imports';

import SustainableProducts from '~/components/homepage/sustainable/sustainable-products.vue';
import BrandWallpaper from '~/components/brand-wallpaper.vue';
import ThemeTeasers from '~/components/homepage/theme-teasers.vue';
import Services from '~/components/homepage/services.vue';
import HomepageCategories from '~/components/homepage/homepage-categories.vue';
import HomepageHighlights from '~/components/homepage/homepage-highlights.vue';
import Promotions from '~/components/promotions.vue';
import RecommendedProducts from '~/components/recommended-products.vue';

import {
  Heading,
  LinkButton,
  GridContainer,
  BannerTeaser,
} from '@i22team/td-longtail-vue-components';
import { Track } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';
import type { StageSliderData } from '~/lib/promotions-type';
import { loadOffersForPromotions, type PromotionOffer } from '~/lib/offers-by-group';
import WarrantyLabel from '~/components/homepage/warranty-label.vue';
import { homepagePromotions } from '~/lib/promotions-homepage';

Track({ page_type: 'theme.landing' });

getHeader({
  title: ref('Smartphones, Tablets und Zubehör'),
  description: ref(
    'Große Auswahl an Handys & Smartphones, Tablets und  Zubehör. Geprüfte Sicherheit ✓ Schnelle Lieferzeiten ✓ Jetzt entdecken und bestellen!'
  ),
});

const { data: promotions, error: errorData } = useLazyAsyncData(
  'homepage-promotions',
  async (): Promise<PromotionOffer<StageSliderData>[]> =>
    loadOffersForPromotions<StageSliderData>(homepagePromotions)
);

// handle errors
if (errorData.value) {
  console.error(errorData.value);
}
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.homepage {
  &__section {
    margin-bottom: 150px;

    &--md {
      margin-bottom: 90px;
    }
  }
}

.coupon {
  &-banner {
    padding-left: $grid-base;
    margin-inline: $grid-base;
    height: 540px;
    align-items: flex-end;
    color: var(--color-font-fixed);
    @include mq(500px) {
      height: 600px;
    }

    @include mq($mq-sm) {
      height: 340px;
      padding-left: 2 * $grid-base;
      align-items: center;
      background-position: top 0 right 30%;
    }
    &__heading {
      @include font-size(36px, 36px);
      margin-bottom: 0.5em;

      @include mq($mq-sm) {
        text-transform: uppercase;
      }
    }

    &__subheading {
      @include font-size(18px, 24px);
    }
  }
}
</style>
